import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import * as moment from "moment";
import axios from "axios";
import { Image, RadioButton, TextInput } from "hesus-ui-kit";
import DatePicker from "react-multi-date-picker";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { daysName } from "@utils/constants";
import editIcon from "@icons/ic-edit-blue.svg";
import yesIcon from "@icons/ic-green-edit.svg";
import cancelIcon from "@icons/ic-edit-cancel.svg";
import AddressSearch from "@components/AddressSearch/AddressSearch";
import ErrorBox from "../ErrorBox";
import {
  GenrealInfo,
  FlexRow,
  GridRow,
  SubTitle,
  HeaderTitle,
} from "./SiteInfo.styled";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
  RequiredMessage,
} from "@utils/extra-style";
import {
  addressSingleLine,
  renderPriceWithCurrencyWithoutUnit,
  addSpaceWithoutFloat,
} from "@utils/utils";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import UserInfo from "@utils/UserInfo";
import RelatedContacts from "../RelatedContacts";
import "../../KickOffView.scss";
import HolderCompany from "@modules/Site/SiteView/components/AddEditFinacialInformation/HolderCompany";
import { loadSingleSite } from "@modules/Site/SiteList/SiteList.slice";

const SiteInfo = (props) => {
  const {
    t,
    data,
    setData,
    koStatus,
    isAuthorizedSalesman,
    siteInfoError,
    setSiteInfoError,
  } = props;

  const dispatch = useDispatch();
  const [isEdit, setEditStatus] = useState(false);
  const [isSiteUpdating, setSiteUpdateStatus] = useState(false);
  const [validateSiteError, setValidateSiteError] = useState(false);
  const generalParams = useGetGeneralParameters();
  const { countryList } = generalParams;
  const updateDaysName = (days) => {
    const daysArr = days.split(",");
    return daysArr.map((day, i) => [" " + t(daysName[day.toUpperCase()])]);
  };

  const countryInfo = UserInfo.countryInfo();

  const countryId = countryInfo.country.id;

  const onEdit = () => {
    setEditStatus(true);
  };

  const language = countryInfo.country.lang;

  const [formData, setFormData] = useState({
    id: {
      required: false,
      value: data?.id ?? null,
      error: "",
    },
    name: {
      required: true,
      value: data?.name ?? "",
      error: "",
    },
    isTender: {
      required: true,
      value: data?.isTender ?? false,
      error: "",
    },
    startDate: {
      required: true,
      value: data?.estimatedStartDate
        ? moment(data.estimatedStartDate, "DD/MM/YYYY").format("YYYY-MM-DD")
        : null,
      error: "",
    },
    endDate: {
      required: true,
      value: data?.estimatedEndDate
        ? moment(data.estimatedEndDate, "DD/MM/YYYY").format("YYYY-MM-DD")
        : null,
      error: "",
    },
    address: {
      required: true,
      value: data?.address ?? "",
      error: "",
    },
    loadingTime: {
      required: true,
      value: data?.loadingTime ?? 15,
      error: "",
    },
    unloadingTime: {
      required: true,
      value: data?.unloadingTime ?? 15,
      error: "",
    },
    simultaneousLoads: {
      required: true,
      value: data?.simultaneousLoads ?? 1,
      error: "",
    },
    simultaneousUnloads: {
      required: true,
      value: data?.simultaneousUnloads ?? 1,
      error: "",
    },
    budgetType: {
      required: true,
      value: data?.budgetType ?? 0,
      error: "",
    },
    siteBudgetAmount: {
      required: true,
      value: data.siteBudgetAmount ?? "",
      error: "",
    },
    paymentDelegationAmount: {
      required: true,
      value: data.paymentDelegationAmount ?? "",
      error: "",
    },
    company: {
      required: true,
      value:
        {
          id: data.delegatedCompany?.id ? data.delegatedCompany?.id : "",
          name: data.delegatedCompany?.id
            ? data.delegatedCompany?.name
            : data.delegatedCompany?.nameText,
        } ?? "",
      error: "",
    },
  });

  useEffect(() => {
    setFormData({
      id: {
        required: false,
        value: data?.id ?? null,
        error: "",
      },
      name: {
        required: true,
        value: data?.name ?? "",
        error: "",
      },
      isTender: {
        required: true,
        value: data?.isTender ?? false,
        error: "",
      },
      startDate: {
        required: true,
        value: data?.estimatedStartDate
          ? moment(data.estimatedStartDate, "DD/MM/YYYY").format("YYYY-MM-DD")
          : null,
        error: "",
      },
      endDate: {
        required: true,
        value: data?.estimatedEndDate
          ? moment(data.estimatedEndDate, "DD/MM/YYYY").format("YYYY-MM-DD")
          : null,
        error: "",
      },
      address: {
        required: true,
        value: data?.address ?? "",
        error: "",
      },
      loadingTime: {
        required: true,
        value: data?.loadingTime ?? 15,
        error: "",
      },
      unloadingTime: {
        required: true,
        value: data?.unloadingTime ?? 15,
        error: "",
      },
      simultaneousLoads: {
        required: true,
        value: data?.simultaneousLoads ?? 1,
        error: "",
      },
      simultaneousUnloads: {
        required: true,
        value: data?.simultaneousUnloads ?? 1,
        error: "",
      },
      budgetType: {
        required: true,
        value: data?.budgetType ?? 0,
        error: "",
      },
      siteBudgetAmount: {
        required: true,
        value: data.siteBudgetAmount ?? "",
        error: "",
      },
      paymentDelegationAmount: {
        required: true,
        value: data.paymentDelegationAmount ?? "",
        error: "",
      },
      company: {
        required: true,
        value:
          {
            id: data.delegatedCompany?.id ? data.delegatedCompany?.id : "",
            name: data.delegatedCompany?.id
              ? data.delegatedCompany?.name
              : data.delegatedCompany?.nameText,
          } ?? "",
        error: "",
      },
    });
  }, [data]);

  const onAddressSelect = (selectedAddress) => {
    const address = {
      street: selectedAddress.street.value,
      zipcode: selectedAddress.postal.value,
      city: selectedAddress.city.value,
      country: selectedAddress.countryId.value,
      position: {
        latitude: selectedAddress.latitude.value,
        longitude: selectedAddress.longitude.value,
      },
    };
    setFormData({
      ...formData,
      address: {
        value: address,
        error: "",
        required: true,
      },
    });
  };

  const onStartDateChange = (data) => {
    setFormData({
      ...formData,
      startDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onEndDateChange = (data) => {
    setFormData({
      ...formData,
      endDate: {
        value: data,
        error: "",
        required: true,
      },
    });
  };

  const onBudgetTypeSelect = (e) => {
    setFormData({
      ...formData,
      budgetType: {
        value: parseInt(e.target.value, 10),
        error: "",
        required: true,
      },
    });
  };

  const onSiteBudgetAmountChange = (e) => {
    setFormData({
      ...formData,
      siteBudgetAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onPaymentDelegationAmountChange = (e) => {
    setFormData({
      ...formData,
      paymentDelegationAmount: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const getFormData = () => {
    let budgetAmount = null;
    let delegatedCompanyInfo = null;
    if (formData.budgetType.value === 1) {
      budgetAmount = formData.siteBudgetAmount.value;
    }
    if (formData.budgetType.value === 2) {
      delegatedCompanyInfo = JSON.stringify({
        id: formData.company.value.id,
        name: formData.company.value.name,
        nameText: !formData.company.value.id
          ? formData.company.value.name
          : null,
      });
    }
    const dataModified = new FormData();
    dataModified.append("id", data?.id ?? null);
    dataModified.append(
      "platformCountry",
      JSON.stringify({ id: data?.platformCountry?.id })
    );
    dataModified.append("isTender", data?.isTender);
    dataModified.append("unloadingTime", data.unloadingTime);
    dataModified.append("simultaneousLoads", data.simultaneousLoads);
    dataModified.append("simultaneousUnloads", data.simultaneousUnloads);
    dataModified.append("loadingTime", data.loadingTime);
    dataModified.append("name", formData.name.value);
    dataModified.append("budgetType", formData.budgetType.value);
    dataModified.append("siteBudgetAmount", formData.siteBudgetAmount.value);
    dataModified.append("delegatedCompany", delegatedCompanyInfo);
    dataModified.append(
      "paymentDelegationAmount",
      formData.budgetType.value === 2
        ? formData.paymentDelegationAmount.value
        : ""
    );
    dataModified.append(
      "company",
      JSON.stringify({
        id: data.company.id,
        name: data.company.name,
      })
    );
    dataModified.append(
      "address",
      JSON.stringify({
        address: formData.address.value.street,
        zipcode: formData.address.value.zipcode,
        city: formData.address.value.city,
        country: formData.address.value.country,
        supplement: data?.address.supplement,
        position: {
          latitude: formData.address.value.position.latitude,
          longitude: formData.address.value.position.longitude,
        },
      })
    );
    dataModified.append("startDate", formData.startDate.value);
    dataModified.append(
      "endDate",
      formData.endDate.value ? formData.endDate.value : formData.startDate.value
    );
    return dataModified;
  };

  // validate form data
  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.startDate.value) {
      isFormValid = false;
      data.startDate.error = t(
        "KICK_OFF_PAGE_SITE_INFO_FORM_START_MISSING_ERROR"
      );
    }
    if (!data.endDate.value) {
      isFormValid = false;
      data.endDate.error = t("KICK_OFF_PAGE_SITE_INFO_FORM_END_MISSING_ERROR");
    }
    if (data.address.value.street === "") {
      isFormValid = false;
      data.address.error = t(
        "KICK_OFF_PAGE_SITE_INFO_FORM_STREET_MISSING_ERROR"
      );
    }
    if (data.budgetType?.value === 1) {
      if (!data.siteBudgetAmount.value && data.siteBudgetAmount.value !== 0) {
        isFormValid = false;
        data.siteBudgetAmount.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (
        data.siteBudgetAmount.value &&
        !/^\d+$/.test(data.siteBudgetAmount.value)
      ) {
        isFormValid = false;
        data.siteBudgetAmount.error = t("INVALID_FORMAT");
      }
    }
    if (data.budgetType?.value === 2) {
      if (
        !data.paymentDelegationAmount.value &&
        data.paymentDelegationAmount.value !== 0
      ) {
        isFormValid = false;
        data.paymentDelegationAmount.error = t("GENERAL_MANDATORY_ERROR");
      }
      if (
        data.paymentDelegationAmount.value &&
        !/^\d+$/.test(data.paymentDelegationAmount.value)
      ) {
        isFormValid = false;
        data.paymentDelegationAmount.error = t("INVALID_FORMAT");
      }
      if (!data.company?.value?.id && !data.company?.value?.name) {
        isFormValid = false;
        data.company.error = t("COMPANY_CREDIT_LIMIT_HOLDER_ERROR");
      }
    }

    if (!isFormValid) {
      setFormData({ ...data });
    }
    return isFormValid;
  };

  const onClose = () => {
    setSiteUpdateStatus(false);
    setEditStatus(false);
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setSiteUpdateStatus(true);
      // setCreateSiteAPIError("");

      axios
        .post(`/sites/${data?.id}`, getFormData())
        .then((response) => {
          setSiteUpdateStatus(false);
          setData({
            ...data,
            address: response?.data[0].address,
            estimatedEndDate: response?.data[0].estimatedEndDate,
            estimatedStartDate: response?.data[0].estimatedStartDate,
            budgetType: response?.data[0].budgetType,
            siteBudgetAmount: response?.data[0].siteBudgetAmount,
            paymentDelegationAmount: response?.data[0].paymentDelegationAmount,
            delegatedCompany: response?.data[0].delegatedCompany,
            company: response?.data[0].company,
          });
          setEditStatus(false);
          setValidateSiteError(true);
          dispatch(loadSingleSite(response?.data[0]));
        })
        .catch((error) => {
          setSiteUpdateStatus(false);
        });
    }
  };

  useEffect(() => {
    let errorInvoice = 0;
    if (validateSiteError && siteInfoError?.length > 0) {
      const siteError = [];
      const sitePostalCode =
        data?.address?.postalCode ?? data?.address?.zipcode;
      if (!sitePostalCode || !data?.address?.city || !data?.address?.street) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_ADDRESS_MISSING_ERROR");
      }
      if (!data.estimatedStartDate || !data.estimatedEndDate) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_DATE_MISSING_ERROR");
      }
      if (data?.workingHours?.length === 0 || !data?.workingHours) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_HOURS_MISSING_ERROR");
      }
      if (![0, 1, 2].includes(data?.budgetType)) {
        siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
        errorInvoice = 1;
      }
      if (data?.budgetType === 1 && errorInvoice === 0) {
        if (!data?.siteBudgetAmount && data?.siteBudgetAmount !== 0) {
          siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
          errorInvoice = 1;
        }
      }
      if (data?.budgetType === 2 && errorInvoice === 0) {
        const delegatedCompanyName = data?.delegatedCompany?.id
          ? data?.delegatedCompany?.name
          : data?.delegatedCompany?.nameText;
        if (
          !data?.paymentDelegationAmount &&
          data?.paymentDelegationAmount !== 0
        ) {
          siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
          errorInvoice = 1;
        }
        if (!delegatedCompanyName) {
          siteError.push("KICK_OFF_PAGE_SITE_INFO_FORM_BUDGET_MISSING_ERROR");
          errorInvoice = 1;
        }
      }
      setSiteInfoError(siteError);
      setValidateSiteError(false);
    }
  }, [validateSiteError]);

  return (
    <GenrealInfo>
      {!isEdit && (
        <>
          {siteInfoError?.length > 0 && <ErrorBox errors={siteInfoError} />}
          <GridRow column="90% auto">
            <HeaderTitle fontWeight="700">
              {t("KICK_OFF_PAGE_SITE_INFO_ADDRESS")}
            </HeaderTitle>
            {koStatus === 1 && isAuthorizedSalesman === 1 && (
              <Image
                src={editIcon}
                alt={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_SAVE")}
                title={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_SAVE")}
                onClick={onEdit}
                styles={{ width: "20px", height: "20px", cursor: "pointer" }}
              />
            )}
          </GridRow>
          <SubTitle color="#767676" paddingLeft="10px">
            {data?.address &&
              addressSingleLine(countryId, data?.address, countryList)}
          </SubTitle>

          <GridRow column="1fr">
            <HeaderTitle fontWeight="700">
              {t("KICK_OFF_PAGE_SITE_INFO_DATES")}
            </HeaderTitle>
          </GridRow>
          <SubTitle color="#767676" paddingLeft="10px">
            {getDateFormat(
              moment(data.estimatedStartDate, "DD/MM/YYYY").format(
                "YYYY-MM-DD"
              ),
              "YYYY-MM-DD"
            ) +
              ` ` +
              t("KICK_OFF_PAGE_SITE_INFO_DATES_AND_OPENING_TO") +
              " " +
              getDateFormat(
                moment(data.estimatedEndDate, "DD/MM/YYYY").format(
                  "YYYY-MM-DD"
                ),
                "YYYY-MM-DD"
              )}
          </SubTitle>
          <GridRow column="1fr">
            <HeaderTitle fontWeight="700">
              {t("KICK_OFF_PAGE_SITE_INFO_BUDGET")}
            </HeaderTitle>
          </GridRow>
          <SubTitle color="#767676" paddingLeft="10px">
            {data?.budgetType === 0 &&
              t("SITE_BUDGET_TYPE_CREDIT_LIMIT") +
                " : " +
                renderPriceWithCurrencyWithoutUnit(
                  data?.company?.creditLimitAmount ||
                    data?.company?.creditLimitAmount === 0
                    ? addSpaceWithoutFloat(data?.company?.creditLimitAmount)
                    : "-",
                  data?.company?.currency?.symbol,
                  data?.company?.currency?.position
                )}
            {data?.budgetType === 1 &&
              t("SITE_BUDGET_TYPE_SITE_BUDGET") +
                " : " +
                renderPriceWithCurrencyWithoutUnit(
                  data?.siteBudgetAmount || data?.siteBudgetAmount === 0
                    ? addSpaceWithoutFloat(data?.siteBudgetAmount)
                    : "-",
                  data?.currency?.symbol,
                  data?.currency?.position
                )}
            {data?.budgetType === 2 && (
              <>
                {t("SITE_BUDGET_TYPE_PAYMENT_DELEGATION") +
                  " : " +
                  renderPriceWithCurrencyWithoutUnit(
                    data?.paymentDelegationAmount ||
                      data?.paymentDelegationAmount === 0
                      ? addSpaceWithoutFloat(data?.paymentDelegationAmount)
                      : "",
                    data?.currency?.symbol,
                    data?.currency?.position
                  )}

                {" ("}
                {data?.delegatedCompany?.id
                  ? data?.delegatedCompany?.name
                  : data?.delegatedCompany?.nameText}
                {")"}
              </>
            )}

            {![0, 1, 2].includes(data?.budgetType) && "-"}
          </SubTitle>
        </>
      )}
      {isEdit && (
        <>
          <GridRow column="85% auto">
            <div></div>
            <div>
              {!isSiteUpdating && (
                <>
                  <Image
                    src={yesIcon}
                    alt={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_SAVE")}
                    title={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_SAVE")}
                    onClick={onSubmit}
                    styles={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                      marginRight: "5px",
                    }}
                  />
                  <Image
                    src={cancelIcon}
                    alt={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_CANCEL")}
                    title={t("KICK_OFF_PAGE_SITE_INFO_FORM_BUTTON_CANCEL")}
                    onClick={onClose}
                    styles={{
                      width: "20px",
                      height: "20px",
                      cursor: "pointer",
                    }}
                  />
                </>
              )}
              {isSiteUpdating && <ClipLoader size={20} color="#1bace4" />}
            </div>
          </GridRow>
          <GridRow column="1fr">
            <AddressSearch
              title="SITE_ADDRESS"
              countryId={countryId}
              name="site-address"
              language={language}
              onAddressSelect={onAddressSelect}
              defaultAddress={
                data.address?.street +
                " " +
                data.address?.zipcode +
                " " +
                data.address?.city +
                " " +
                (data.platformCountry?.name ? data.platformCountry?.name : "")
              }
              error={formData.address.error}
            />
          </GridRow>

          <GridRow column="1fr 1fr">
            <div
              className={
                formData.startDate.error
                  ? "add-site__date-container_start_error"
                  : "add-site__date-container_start add-site__date-container_start_required"
              }
            >
              <label htmlFor="winning-date">
                {t("SITE_ESTIMATED_START_DATE")}
              </label>
              <DatePicker
                style={{
                  padding: "5px 16px",
                  backgroundColor: "white",
                  borderColor: "hsl(0deg, 0%, 80%)",
                  borderRadius: "4px",
                  fontFamily: "Lato",
                  fontSize: "16px",
                  height: "auto",
                  width: "120%",
                  lineHeight: "1.4",
                  border: "1px solid #474747",
                }}
                title={t("SITE_ESTIMATED_START_DATE")}
                calendarPosition="bottom-center"
                format={getDatePickerFormat()}
                locale={getCalendarLocale()}
                value={
                  getDateFormat(formData.startDate.value, "YYYY-MM-DD") || ""
                }
                onChange={(e) => {
                  onStartDateChange(moment(e.toDate()).format("YYYY-MM-DD"));
                }}
                name="start-date"
              />
              {formData.startDate.error && (
                <RequiredMessage>{formData.startDate.error}</RequiredMessage>
              )}
            </div>
            <div
              className={
                formData.startDate.error
                  ? "add-site__date-container_start_error"
                  : "add-site__date-container_start add-site__date-container_start_required"
              }
            >
              <label htmlFor="winning-date">
                {t("SITE_ESTIMATED_END_DATE")}
              </label>
              <DatePicker
                style={{
                  padding: "5px 16px",
                  backgroundColor: "white",
                  borderColor: "hsl(0deg, 0%, 80%)",
                  borderRadius: "4px",
                  fontFamily: "Lato",
                  fontSize: "16px",
                  height: "auto",
                  width: "120%",
                  lineHeight: "1.4",
                  border: "1px solid #474747",
                }}
                title={t("SITE_ESTIMATED_END_DATE")}
                calendarPosition="bottom-center"
                format={getDatePickerFormat()}
                locale={getCalendarLocale()}
                value={
                  getDateFormat(formData.endDate.value, "YYYY-MM-DD") || ""
                }
                onChange={(e) => {
                  onEndDateChange(moment(e.toDate()).format("YYYY-MM-DD"));
                }}
                name="start-date"
              />
              {formData.endDate.error && (
                <RequiredMessage>{formData.endDate.error}</RequiredMessage>
              )}
            </div>
          </GridRow>
          <GridRow column="1fr">
            <HeaderTitle>{t("SITE_BUDGET_TYPE")}</HeaderTitle>
          </GridRow>
          <GridRow column="1fr" className="kickoff-view-solution__radio-button">
            <RadioButton
              value={formData.budgetType.value}
              onChange={onBudgetTypeSelect}
              name="price-type"
              options={[
                {
                  label: t("SITE_BUDGET_TYPE_CREDIT_LIMIT"),
                  value: 0,
                },
                {
                  label: t("SITE_BUDGET_TYPE_SITE_BUDGET"),
                  value: 1,
                },
                {
                  label: t("SITE_BUDGET_TYPE_PAYMENT_DELEGATION"),
                  value: 2,
                },
              ]}
            />
          </GridRow>
          {formData.budgetType.value === 1 && (
            <GridRow column="1fr 1fr">
              <TextInput
                id="siteBudgetAmount"
                type="text"
                label={t("SITE_BUDGET_AMOUNT")}
                isFloat
                required
                value={String(formData.siteBudgetAmount.value)}
                onChange={(e) => onSiteBudgetAmountChange(e)}
                containerStyles={{ marginTop: "21px" }}
                errorStyles={{ "font-style": "italic" }}
                labelStyles={
                  formData.siteBudgetAmount.error !== ""
                    ? labelStylesError
                    : labelStyles
                }
                inputStyles={
                  formData.siteBudgetAmount.error !== ""
                    ? inputStylesError
                    : inputStyles
                }
                error={formData.siteBudgetAmount.error}
                placeholder={t("SITE_BUDGET_AMOUNT")}
              />
            </GridRow>
          )}
          {formData.budgetType.value === 2 && (
            <GridRow column="1fr 1fr">
              <div>
                <HolderCompany
                  formData={formData}
                  setFormData={setFormData}
                  selectedHolderCompany={data?.delegatedCompany}
                  objectId={data?.delegatedCompany?.id}
                />
              </div>
              <TextInput
                id="paymentDelegationAmount"
                type="number"
                label={t("SITE_DELEGATION_AMOUNT")}
                isFloat
                required
                value={String(formData.paymentDelegationAmount.value)}
                onChange={(e) => onPaymentDelegationAmountChange(e)}
                containerStyles={{ marginTop: "21px" }}
                errorStyles={{ "font-style": "italic" }}
                labelStyles={
                  formData.paymentDelegationAmount.error !== ""
                    ? labelStylesError
                    : labelStyles
                }
                inputStyles={
                  formData.paymentDelegationAmount.error !== ""
                    ? inputStylesError
                    : inputStyles
                }
                error={formData.paymentDelegationAmount.error}
                placeholder={t("SITE_DELEGATION_AMOUNT")}
              />
            </GridRow>
          )}
        </>
      )}

      <GridRow column="1fr">
        <HeaderTitle fontWeight="700">
          {t("KICK_OFF_PAGE_SITE_INFO_OPENING_HOURS")}
        </HeaderTitle>
      </GridRow>

      {data?.workingHours?.length > 0 &&
        data.workingHours.map((hour) => {
          return (
            <FlexRow key={"workinghour-company-" + hour.id}>
              <SubTitle color="#767676">
                {hour.openingHour +
                  " - " +
                  hour.closingHour +
                  " /" +
                  updateDaysName(hour.selectedDays)}
              </SubTitle>
            </FlexRow>
          );
        })}

      <GridRow column="1fr">
        <HeaderTitle fontWeight="700">
          {t("KICK_OFF_PAGE_SITE_INFO_CONTACTS")}
        </HeaderTitle>
      </GridRow>
      {data?.contacts && (
        <RelatedContacts contactList={data?.contacts} page="relatedContact" />
      )}
    </GenrealInfo>
  );
};
SiteInfo.propTypes = {
  t: PropTypes.func.isRequired,
  data: PropTypes.shape({}).isRequired,
  koStatus: PropTypes.number.isRequired,
  isAuthorizedSalesman: PropTypes.number.isRequired,
};
SiteInfo.defaultProps = {
  // data: { adminComment: null, internalComment: null },
};

export default withTranslation()(SiteInfo);
